import React, { useCallback, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import styled from 'styled-components';
import { compose } from 'redux';
import WithBasicSearch, { IWithBasicSearchProps } from 'hoc/WithBasicSearch';
import { IWithTravelAgentsDataProps, withTravelAgentsData } from 'hoc/WithTravelAgentsData';
import PredictiveTextInput from 'pureUi/PredictiveTextInput';
import { ApiCategoryDropdown } from 'ui/ApiCategoryDropdown/ApiCategoryDropdown';
import { LodgingsEditor } from 'pureUi/LodgingsEditor';
import DateRangeInput from 'pureUi/DateRangeInput';
import { PrimaryButton } from 'pureUi/Buttons';
import { pureUiTheme } from 'pureUi/pureUiTheme';
import Checkbox from 'pureUi/Checkbox';
import { DatePickerStateProvider, IDatePickerSateParams } from 'pureUi/providers/DatePickerStateProvider';
import { SvgIcon } from 'ui/SvgIcon';
import LoaderIcon from 'ui/Icons/tabler/loader.component.svg';
import { getBootstrapCountriesSelector } from 'store/modules/bootstrap/selectors';
import { useSelector } from 'react-redux';
import { Multiselect } from 'ui/Multiselect';
import { TCountryCode } from 'interfaces';
import * as AuthSelectors from 'store/modules/auth/selectors';
import SearchIcon from 'ui/Icons/search.component.svg';
import RadioButton from 'pureUi/RadioButton';
import { theme } from '../../../tailwind.config';
import { ICompanyMembership } from 'services/BookingManagerApi/types/CompanyMemberships';
import InfoIcon from 'ui/Icons/components/Info.component';
import { ModalV1 } from 'ui/ModalV1';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';
import classNames from 'classnames';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import DatePicker from 'pureUi/DatePicker';
import { MobileBottomDrawer } from 'ui/MobileBottomDrawer/MobileBottomDrawer';

export interface ISearchBarProps extends IWithBasicSearchProps, IWithTravelAgentsDataProps {
  className: string;
  isSearchIconVisible: boolean;
}

export const SearchBar = (props: ISearchBarProps) => {
  const { dynamicParameters } = useDynamicParameters();
  const [membershipInModal, setMembershipInModal] = useState<ICompanyMembership | null>(null);

  const [showLodgingControls, setShowLodgingControls] = useState(false);
  const [isDateDrawerOpen, setIsDateDrawerOpen] = useState(false);
  const dateSelectionRef = useRef(false);
  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;

  const countries = useSelector(getBootstrapCountriesSelector);
  const isTa = useSelector(AuthSelectors.isTA);
  const isSr = useSelector(AuthSelectors.isInternalUser);

  const showClientCountryCode = !isTa || (isTa && dynamicParameters.TA_USE_CLIENT_COUNTRY_CODE);

  const closeMembershipModal = useCallback(() => {
    setMembershipInModal(null);
  }, []);

  return (
    <div className={props.className}>
      <div
        className={classNames('grid gap-4 grid-cols-1', {
          'lg:grid-cols-[1fr_1fr_1fr_124px_215px]': isSr,
          'lg:grid-cols-[1fr_1fr_1fr_1fr_215px]': !isSr && dynamicParameters.TA_USE_CLIENT_COUNTRY_CODE,
          'lg:grid-cols-[1fr_1fr_1fr_215px]': !isSr && !dynamicParameters.TA_USE_CLIENT_COUNTRY_CODE,
        })}
      >
        <label>
          <span>Destination or Resort</span>
          <ApiCategoryDropdown
            nameSearchResults={props.nameSearchResultsRaw}
            value={props.searchQuery.name || ''}
            onChange={text => props.destinationChange(text)}
            isLoading={props.nameSearchPending}
            placeholder="Where to"
          />
        </label>

        <label>
          <span>Dates * </span>
          <DatePickerStateProvider
            key={props.initialized}
            defaultSelectedDates={props.selectedDates}
            onDateChange={props.basicSearchHandlers.handleDateChange}
            render={(params: IDatePickerSateParams) => {
              if (isMobileOrTablet) {
                if (dateSelectionRef.current && !params.dateSelectionInProgress && isDateDrawerOpen) {
                  setTimeout(() => setIsDateDrawerOpen(false), 200);
                }
                dateSelectionRef.current = params.dateSelectionInProgress;

                return (
                  <>
                    <button
                      type="button"
                      onClick={() => setIsDateDrawerOpen(true)}
                      className="flex items-center border border-gray-17 uppercase px-[10px] font-hurmegeometric-sans text-sm text-black text-left w-full h-[39px] bg-white"
                    >
                      <span className="flex-grow whitespace-nowrap overflow-hidden text-ellipsis">{params.displayString}</span>
                      <span className="bg-teal-30 px-[10px] py-[5px] rounded text-xs shadow-sm flex-shrink-0 ml-[10px]">
                        {params.totalNights} {params.totalNights === 1 ? 'Night' : 'Nights'}
                      </span>
                    </button>
                    <MobileBottomDrawer isOpen={isDateDrawerOpen} onClose={() => setIsDateDrawerOpen(false)}>
                      <div className="flex justify-between items-center py-[15px] border-b border-gray-17 mb-[15px]">
                        <span className="font-hurmegeometric-sans text-sm uppercase">Dates</span>
                        <div className="flex items-center gap-[10px]">
                          <span className="font-hurmegeometric-sans text-sm uppercase">{params.displayString}</span>
                          <span className="bg-teal-30 px-[10px] py-[5px] rounded font-hurmegeometric-sans text-xs shadow-sm">
                            {params.totalNights} {params.totalNights === 1 ? 'Night' : 'Nights'}
                          </span>
                        </div>
                      </div>
                      <div className="[&>*]:!w-full [&>*]:!shadow-none [&_.calendars]:!w-full [&_.calendar]:!w-full [&_.calendarGrid]:!w-auto">
                        <DatePicker
                          calendarCount={1}
                          currentDate={params.datePickerCurrentDate}
                          selectedDates={params.selectedDates}
                          onDayClick={(date: string) => {
                            params.handleDayClick(date);
                          }}
                          onDayMouseOver={params.handleDateMouseOver}
                          onNextClick={params.incrementDate}
                          onPrevClick={params.decrementDate}
                        />
                      </div>
                    </MobileBottomDrawer>
                  </>
                );
              }
              return (
                <DateRangeInput
                  className="serachBarDateRangeInput"
                  displayString={params.displayString}
                  currentDate={params.datePickerCurrentDate}
                  totalNights={params.totalNights}
                  selectedDates={params.selectedDates}
                  onDayClick={params.handleDayClick}
                  onDayMouseOver={params.handleDateMouseOver}
                  showDatePicker={params.showDatePicker}
                  onNextClick={params.incrementDate}
                  onPrevClick={params.decrementDate}
                  onMouseDown={params.toggleDatePicker}
                  onClickOutside={params.hideDatePicker}
                  noPortal
                />
              );
            }}
          />
        </label>

        <label>
          <span>Rooms *</span>
          <LodgingsEditor
            className="lodging-editor"
            showControls={showLodgingControls}
            lodgings={props.searchQuery.lodgings}
            activeLodgingIndex={props.activeLodingIndex}
            onIncrementIndex={props.incrementActiveLodgingIndex}
            onTabSelect={props.setActiveLodgingIndex}
            onIncrementRoomCount={props.incrementRoom}
            onIncrementAdultCount={props.incrementAdult}
            onIncrementChildCount={props.incrementChild}
            onChildAgeChange={props.setAge}
            totalGuestCount={props.totalGuestCount}
            onClick={() => {
              setShowLodgingControls(true);
            }}
            onClickOutside={() => {
              setShowLodgingControls(false);
            }}
          />
        </label>

        {isTa && showClientCountryCode && (
          <label>
            <span>Guest country</span>
            <Multiselect
              className={'guest-country bg-white min-w-290px font-hurmegeometric-sans'}
              placeholderText="SELECT COUNTRY"
              borderColourClass="border-gray-17"
              itemsClassname="uppercase text-black"
              itemContentClassName="uppercase text-black"
              fontClass="font-hurmegeometric-sans"
              itemCtaClassName="uppercase bg-white hover:bg-blue-5"
              openClassName="outline outline-2 outline-teal-100"
              placeholderClasses="not-italic text-gray-15"
              height={37}
              onUpdate={sv => {
                if (sv[0]) {
                  props.guestCountryChangeAction(sv[0] as TCountryCode);
                } else {
                  props.guestCountryChangeAction(null);
                }
              }}
              hideCheckboxes={true}
              isSingleSelectMode={true}
              isCloseOnSelect={true}
              isEnableFuzzySearch={true}
              fuzzySearchInputActiveClass="bg-white"
              options={countries.map(country => ({
                value: country.code,
                label: country.name,
              }))}
              selectedValues={props.searchQuery.clientCountryCode ? [props.searchQuery.clientCountryCode] : []}
            />
          </label>
        )}

        {props.isSr && (
          <label className="repeatGuest order-[7] lg:order-none">
            <span>Repeat Guest</span>
            <Checkbox checked={props.isRepeatGuest} onChange={props.toggleRepeatGuest} />
          </label>
        )}

        <PrimaryButton
          className={classNames('searchButton order-last lg:order-none lg:mt-1')}
          disabled={!props.canSearch || !props.canSrSearch}
          onClick={props.basicSearchHandlers.handleNavigateToSearch}
        >
          <span className={classnames('flex items-center', { 'justify-center': !props.isSearchIconVisible || isMobileOrTablet })}>
            {props.isSearchIconVisible && (
              <SearchIcon className="block cursor-pointer ml-15px mr-2" fill={theme.colors['white']} />
            )}
            <span>Search</span>
            {!!props.isLoading && (
              <SvgIcon
                IconComponent={LoaderIcon}
                className="w-4 h-4 fill-transparent stroke-current stroke-2 animate-spin ml-2"
              />
            )}
          </span>
        </PrimaryButton>

        {props.isSr && (
          <label className="self-start">
            <span>Company</span>
            {props.isFetchingCompanies ? (
              <span>Loading companies...</span>
            ) : (
              <PredictiveTextInput
                placeholder="Select company..."
                value={props.companyNameSearch}
                onChange={e => props.searchCompanyByName(e.currentTarget.value)}
                options={[props.companiesNames]}
                onOptionSelect={props.handleCompanyNameChange}
                showDropDown={props.showCompanyDropdown}
                onFocus={() => props.showCompanyDropdownChange(true)}
                onBlur={() => props.showCompanyDropdownChange(false)}
              />
            )}
          </label>
        )}
        {props.isSr && props.selectedCompany && (
          <label className="self-start">
            <span>Travel Agent</span>
            {props.isFetchingTA ? (
              <span>Loading travel agents...</span>
            ) : (
              <PredictiveTextInput
                placeholder="Select agent..."
                value={props.taNameSearch}
                onChange={e => props.searchTaByName(e.currentTarget.value)}
                options={[props.taNames]}
                onOptionSelect={props.handleTaNameChange}
                showDropDown={props.showTaDropdown}
                onFocus={() => props.showTaDropdownChange(true)}
                onBlur={() => props.showTaDropdownChange(false)}
              />
            )}
          </label>
        )}
        {isSr && showClientCountryCode && (
          <label className="self-start">
            <span>Guest country</span>
            <Multiselect
              className={'guest-country bg-white min-w-290px font-hurmegeometric-sans'}
              placeholderText="SELECT COUNTRY"
              borderColourClass="border-gray-17"
              itemsClassname="uppercase text-black"
              itemContentClassName="uppercase text-black"
              fontClass="font-hurmegeometric-sans"
              itemCtaClassName="uppercase bg-white hover:bg-blue-5"
              openClassName="outline outline-2 outline-teal-100"
              placeholderClasses="not-italic text-gray-15"
              height={37}
              onUpdate={sv => {
                if (sv[0]) {
                  props.guestCountryChangeAction(sv[0] as TCountryCode);
                } else {
                  props.guestCountryChangeAction(null);
                }
              }}
              hideCheckboxes={true}
              isSingleSelectMode={true}
              isCloseOnSelect={true}
              isEnableFuzzySearch={true}
              fuzzySearchInputActiveClass="bg-white"
              options={countries.map(country => ({
                value: country.code,
                label: country.name,
              }))}
              selectedValues={props.searchQuery.clientCountryCode ? [props.searchQuery.clientCountryCode] : []}
            />
          </label>
        )}
        {props.companyMemberships && props.companyMemberships.length > 0 && (
          <React.Fragment>
            <div
              className="company-memberships"
              style={
                isMobileOrTablet
                  ? undefined
                  : {
                      gridColumn: props.isSr ? '4/6' : '1/5', // unfortunately have to hardcode this because of the way the grid is set up
                    }
              }
            >
              <span className="uppercase text-xs block mb-10px">Membership</span>
              <div className="memberships flex flex-row flex-wrap gap-3">
                {props.companyMemberships.map(membership => {
                  return (
                    <div key={membership.uuid} className="membership-radio flex flex-row items-center space-x-8px">
                      <RadioButton
                        name="chooseRegions"
                        type="radio"
                        checked={membership.uuid === props.selectedCompanyMembership?.uuid}
                        onChange={() => {
                          props.setSelectedCompanyMembership(membership);
                        }}
                      />
                      <span className="font-hurmegeometric-sans text-black-legacy text-sm font-normal space-x-5px flex items-center">
                        <span>{membership.name}</span>
                        {membership.benefitsDescription && (
                          <button
                            onClick={() => {
                              setMembershipInModal(membership);
                            }}
                            className="show-membership-info-modal bg-none bg-transparent outline-none border-non p-0 m-0 cursor-pointer flex justify-center items-center"
                          >
                            <SvgIcon
                              IconComponent={InfoIcon}
                              defaultFill={theme.colors['gray-40']}
                              hoverFill={theme.colors['gray-80']}
                              activeFill={theme.colors['gray-40']}
                              width="18px"
                              height="18px"
                            />
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            {membershipInModal && (
              <ModalV1
                className="membership-modal min-w-[500px] max-w-[500px]"
                title={membershipInModal.name}
                message={membershipInModal.benefitsDescription ?? ''}
                onClose={closeMembershipModal}
              />
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

export const StyledSearchBar = styled(SearchBar)`
  color: ${pureUiTheme.colorRoles.grayLabel};
  text-align: left;
  margin: auto;

  .grid {
    display: grid;
    grid-column-gap: 1rem;
    grid-row-gap: 1rem;
    align-items: end;
  }

  label > span {
    text-transform: uppercase;
    font-size: 12px;
    display: block;
    margin-bottom: 10px;
  }

  .searchButton {
    height: 39px;
    margin: 0;
  }

  .repeatGuest {
    display: flex;
    flex-direction: row-reverse;
    gap: 10px;
    justify-content: flex-end;
    align-items: center;
  }

  .serachBarDateRangeInput {
    width: 100%;
  }
`;

export default compose(WithBasicSearch(), withTravelAgentsData())(StyledSearchBar);
